import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown } from "lucide-react";
import SectionHeading from "./SectionHeading";
import { WHATSAPP_LINK, SUPPORT_EMAIL, WHATSAPP_NUMBER_DISPLAY } from "../config";

const FAQS = [
    {
        q: "How do I get access after paying?",
        a: "The moment your payment succeeds, a download link for the complete bundle is sent to your email and WhatsApp — usually within 2 minutes.",
    },
    {
        q: "Which formats are the eBooks in?",
        a: "Almost all eBooks come as PDF and EPUB files, so they open on any device — no special software needed.",
    },
    {
        q: "Can I read on my mobile phone?",
        a: "Yes. The bundle is designed for mobile reading — open the files in any PDF/EPUB reader on Android or iPhone, or on tablet, PC and Kindle.",
    },
    {
        q: "Is it really a one-time payment?",
        a: "Yes. Pay ₹199 once and the bundle is yours for life — no subscription, no renewals, no hidden charges.",
    },
    {
        q: "What if I don't receive the link?",
        a: "Just message us on WhatsApp with your payment screenshot and we'll resend the link within minutes, day or night.",
    },
    {
        q: "What is the refund policy?",
        a: "We offer a 24-hour money-back guarantee. If you can't access the bundle — or simply don't love it — message us on WhatsApp with your payment screenshot within 24 hours of purchase and you'll get a full refund. No questions asked.",
    },
    {
        q: "How do I contact support?",
        a: `WhatsApp us at ${WHATSAPP_NUMBER_DISPLAY} or email ${SUPPORT_EMAIL} — we reply fast, usually within an hour.`,
    },
    {
        q: "Are future updates really free?",
        a: "Yes. New titles and categories are added regularly, and every future update is included at no extra cost — forever.",
    },
];

export default function Faq() {
    const [open, setOpen] = useState(0);

    return (
        <section
            id="faq"
            data-testid="faq-accordion"
            className="scroll-mt-24 py-16 sm:py-24"
        >
            <div className="mx-auto max-w-3xl px-4 sm:px-6">
                <SectionHeading
                    eyebrow="FAQ"
                    title="Questions, answered"
                    sub="Everything readers ask before they grab the bundle."
                />
                <div className="mt-10 space-y-3">
                    {FAQS.map((f, i) => (
                        <div
                            key={f.q}
                            data-testid="faq-item"
                            className="overflow-hidden rounded-2xl border border-emerald-100 bg-white shadow-soft"
                        >
                            <button
                                data-testid={`faq-question-${i}`}
                                onClick={() => setOpen(open === i ? -1 : i)}
                                aria-expanded={open === i}
                                className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left sm:px-6 sm:py-5"
                            >
                                <span className="text-sm font-bold text-slate-900 sm:text-base">
                                    {f.q}
                                </span>
                                <ChevronDown
                                    size={18}
                                    className={`shrink-0 text-emerald-600 transition-transform duration-300 ${
                                        open === i ? "rotate-180" : ""
                                    }`}
                                />
                            </button>
                            <AnimatePresence initial={false}>
                                {open === i && (
                                    <motion.div
                                        key="content"
                                        initial={{ height: 0, opacity: 0 }}
                                        animate={{ height: "auto", opacity: 1 }}
                                        exit={{ height: 0, opacity: 0 }}
                                        transition={{
                                            duration: 0.3,
                                            ease: "easeInOut",
                                        }}
                                        className="overflow-hidden"
                                    >
                                        <p className="px-5 pb-5 text-sm leading-relaxed text-slate-600 sm:px-6 sm:text-base">
                                            {f.a}
                                        </p>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    ))}
                </div>
                <p className="mt-6 text-center text-sm text-slate-500">
                    Still unsure?{" "}
                    <a
                        href={WHATSAPP_LINK}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="faq-whatsapp-link"
                        className="font-semibold text-emerald-700 underline underline-offset-4 hover:text-emerald-800"
                    >
                        Ask us on WhatsApp
                    </a>{" "}
                    — we reply before you finish a chapter.
                </p>
            </div>
        </section>
    );
}
